import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { poAPI } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Separator } from '@/components/ui/separator';
import { Skeleton } from '@/components/ui/skeleton';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import {
  ArrowLeft, Copy, Mail, Send, CheckCircle2, Package, Truck,
  Calendar, FileText, AlertTriangle, XCircle, Printer,
} from 'lucide-react';
import { VendorScoreWarning } from '@/components/VendorScorecard';
import { generatePurchaseOrderPDF, downloadPDF } from '@/lib/pdfGenerator';

const FABRIC_QC_CHECKS = [
  { check_name: 'Fabric code matches PO', key: 'code_match' },
  { check_name: 'Colour matches expectation', key: 'colour_match' },
  { check_name: 'Quantity matches ordered', key: 'qty_match', auto: true },
  { check_name: 'No stains', key: 'no_stains' },
  { check_name: 'No weaving faults', key: 'no_weaving_faults' },
  { check_name: 'No transit damage', key: 'no_transit_damage' },
];

const STATUS_COLORS = {
  draft: { bg: 'rgba(107,114,128,0.10)', text: '#6B7280', border: 'rgba(107,114,128,0.25)' },
  sent: { bg: 'rgba(37,99,235,0.08)', text: '#2563EB', border: 'rgba(37,99,235,0.25)' },
  acknowledged: { bg: 'rgba(6,182,212,0.08)', text: '#0891B2', border: 'rgba(6,182,212,0.25)' },
  partially_received: { bg: 'rgba(245,158,11,0.10)', text: '#D97706', border: 'rgba(245,158,11,0.25)' },
  fully_received: { bg: 'rgba(16,185,129,0.10)', text: '#059669', border: 'rgba(16,185,129,0.30)' },
  cancelled: { bg: 'rgba(239,68,68,0.08)', text: '#DC2626', border: 'rgba(239,68,68,0.25)' },
};
const STATUS_LABELS = {
  draft: 'Draft', sent: 'Sent', acknowledged: 'Acknowledged',
  partially_received: 'Partially Received', fully_received: 'Fully Received', cancelled: 'Cancelled',
};

function StatusBadge({ status }) {
  const c = STATUS_COLORS[status] || STATUS_COLORS.draft;
  return (
    <Badge variant="outline" className="text-[10px] font-semibold px-2 py-0.5 rounded-md"
      style={{ backgroundColor: c.bg, color: c.text, borderColor: c.border }}>
      {STATUS_LABELS[status] || status}
    </Badge>
  );
}

export default function PODetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [po, setPo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [receiveDialog, setReceiveDialog] = useState(null);
  const [recvQty, setRecvQty] = useState('');
  const [recvDate, setRecvDate] = useState(new Date().toISOString().split('T')[0]);
  const [receiving, setReceiving] = useState(false);
  const [statusUpdating, setStatusUpdating] = useState(false);
  const [qcStep, setQcStep] = useState(false); // false = qty step, true = QC checklist step
  const [qcChecks, setQcChecks] = useState({});
  const [qcNotes, setQcNotes] = useState('');

  const fetchPO = useCallback(async () => {
    try {
      const res = await poAPI.get(id);
      setPo(res.data);
    } catch (err) {
      toast.error('PO not found');
    }
    setLoading(false);
  }, [id]);

  useEffect(() => { fetchPO(); }, [fetchPO]);

  const handleStatusChange = async (newStatus) => {
    setStatusUpdating(true);
    try {
      const res = await poAPI.updateStatus(id, { status: newStatus });
      setPo(res.data);
      toast.success(`Status updated to ${STATUS_LABELS[newStatus]}`);
    } catch (err) {
      toast.error('Failed to update status');
    }
    setStatusUpdating(false);
  };

  const handleCopyWhatsApp = async () => {
    try {
      const res = await poAPI.whatsappText(id);
      await navigator.clipboard.writeText(res.data.text);
      toast.success('Copied to clipboard');
    } catch (err) {
      toast.error('Failed to copy');
    }
  };

  const handleEmail = () => {
    if (!po) return;
    const subject = encodeURIComponent(`PO# ${po.po_number} - Fabric Order`);
    const body = encodeURIComponent(
      `Dear ${po.vendor_name},\n\nPlease find attached Purchase Order ${po.po_number}.\n\nRegards,\nHerringbone & Sui`
    );
    window.open(`mailto:?subject=${subject}&body=${body}`);
  };

  const openReceiveDialog = (line) => {
    setReceiveDialog(line);
    setRecvQty(String(line.quantity_metres));
    setQcStep(false);
    setQcNotes('');
    // Pre-fill QC checks: all pass by default, auto-flag qty mismatch
    const initChecks = {};
    FABRIC_QC_CHECKS.forEach(c => { initChecks[c.key] = 'pass'; });
    setQcChecks(initChecks);
  };

  const handleProceedToQC = () => {
    if (!recvQty) return;
    // Auto-flag quantity mismatch
    const ordered = receiveDialog?.quantity_metres || 0;
    const received = parseFloat(recvQty) || 0;
    if (Math.abs(received - ordered) > 0.01) {
      setQcChecks(prev => ({ ...prev, qty_match: 'fail' }));
    }
    setQcStep(true);
  };

  const handleReceive = async () => {
    if (!receiveDialog || !recvQty) return;
    setReceiving(true);
    try {
      const qcChecksList = FABRIC_QC_CHECKS.map(c => ({
        check_name: c.check_name,
        result: qcChecks[c.key] || 'pass',
        notes: '',
      }));
      const res = await poAPI.receiveLineItem(receiveDialog.id, {
        received_quantity: parseFloat(recvQty),
        received_date: recvDate,
        qc_checks: qcChecksList,
        qc_notes: qcNotes,
      });
      const qcResult = res.data?.qc_result;
      if (qcResult?.overall_result === 'pass') {
        toast.success('Fabric received & QC passed');
      } else if (qcResult?.overall_result === 'fail') {
        toast.warning('Fabric received but QC FAILED — flagged for review');
      } else {
        toast.success('Item received');
      }
      setReceiveDialog(null);
      setRecvQty('');
      setQcStep(false);
      fetchPO();
    } catch (err) {
      toast.error('Failed to record receipt');
    }
    setReceiving(false);
  };

  if (loading) return <div className="space-y-6"><Skeleton className="h-10 w-48" /><Skeleton className="h-64 w-full" /></div>;
  if (!po) return <div className="text-center py-20"><p>PO not found</p></div>;

  const company = po.company_info || {};
  const lines = po.line_items || [];
  const receivedCount = lines.filter(l => l.received_quantity != null).length;
  const allReceived = receivedCount === lines.length && lines.length > 0;
  const hasGst = (po.gst_rate_pct || 0) > 0;

  return (
    <div className="space-y-6 max-w-4xl mx-auto">
      {/* Header */}
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <button onClick={() => navigate('/app/purchase-orders')} className="flex items-center gap-1 text-xs font-medium mb-2 hover:underline"
            style={{ color: 'var(--ink-muted)' }} data-testid="back-to-pos">
            <ArrowLeft className="w-3.5 h-3.5" /> Purchase Orders
          </button>
          <div className="flex items-center gap-3">
            <h1 className="text-2xl sm:text-3xl font-semibold" style={{ color: 'var(--brand-navy)' }} data-testid="po-number-heading">
              {po.po_number}
            </h1>
            <StatusBadge status={po.status} />
          </div>
          <div className="flex items-center gap-3 mt-1 text-sm" style={{ color: 'var(--ink-muted)' }}>
            <span className="flex items-center gap-1"><Truck className="w-3.5 h-3.5" /> {po.vendor_name}</span>
            <span className="flex items-center gap-1"><Calendar className="w-3.5 h-3.5" /> {po.po_date}</span>
            <span>{receivedCount}/{lines.length} received</span>
          </div>
          {/* Vendor Score Banner */}
          <div className="mt-2"><VendorScoreWarning vendorName={po.vendor_name} /></div>
        </div>
        <div className="flex items-center gap-2 flex-wrap no-print">
          <Button variant="outline" size="sm" className="gap-1 rounded-lg text-xs" 
            onClick={() => {
              const doc = generatePurchaseOrderPDF({ ...po, items: lines });
              downloadPDF(doc, `PO-${po.po_number || po.id?.slice(-8)}.pdf`);
              toast.success('Purchase Order PDF downloaded');
            }} data-testid="print-po">
            <Printer className="w-3 h-3" /> Download PO
          </Button>
          <Button variant="outline" size="sm" className="gap-1 rounded-lg text-xs" onClick={handleCopyWhatsApp} data-testid="copy-whatsapp">
            <Copy className="w-3 h-3" /> WhatsApp Copy
          </Button>
          <Button variant="outline" size="sm" className="gap-1 rounded-lg text-xs" onClick={handleEmail} data-testid="send-email">
            <Mail className="w-3 h-3" /> Email
          </Button>
          {po.status === 'draft' && (
            <Button size="sm" className="gap-1 rounded-lg text-xs"
              style={{ backgroundColor: 'var(--brand-navy)' }}
              onClick={() => handleStatusChange('sent')} disabled={statusUpdating}
              data-testid="mark-sent">
              <Send className="w-3 h-3" /> Mark Sent
            </Button>
          )}
          {po.status !== 'draft' && po.status !== 'fully_received' && po.status !== 'cancelled' && (
            <Select value={po.status} onValueChange={handleStatusChange} disabled={statusUpdating}>
              <SelectTrigger className="h-8 w-[160px] rounded-lg text-xs" data-testid="po-status-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {Object.entries(STATUS_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
              </SelectContent>
            </Select>
          )}
        </div>
      </div>

      {/* Professional PO Document */}
      <Card className="rounded-xl border border-border/80 bg-white shadow-[0_1px_0_rgba(16,24,40,0.04),0_10px_24px_rgba(16,24,40,0.06)]" data-testid="po-document">
        <CardContent className="p-6">
          {/* PO Header */}
          <div className="flex justify-between items-start mb-6">
            <div>
              <h2 className="text-lg font-bold" style={{ color: 'var(--brand-navy)', fontFamily: '"Bodoni Moda", Georgia, serif' }}>
                PURCHASE ORDER
              </h2>
              <p className="text-sm font-semibold mt-1" style={{ color: 'var(--brand-navy)' }}>{po.po_number}</p>
              <p className="text-xs mt-0.5" style={{ color: 'var(--ink-muted)' }}>Date: {po.po_date}</p>
            </div>
            <div className="text-right">
              <p className="text-sm font-semibold" style={{ color: 'var(--brand-navy)' }}>{company.name}</p>
              <p className="text-xs" style={{ color: 'var(--ink-muted)' }}>{company.address}</p>
            </div>
          </div>

          {/* To Vendor */}
          <div className="mb-5 p-3 rounded-lg" style={{ backgroundColor: 'rgba(27,42,74,0.02)' }}>
            <p className="text-[10px] uppercase font-medium" style={{ color: 'var(--ink-muted)' }}>To</p>
            <p className="text-sm font-semibold" style={{ color: 'var(--navy-ink)' }}>{po.vendor_name}</p>
          </div>

          {/* Line Items Table */}
          <div className="rounded-lg border overflow-hidden" style={{ borderColor: 'rgba(27,42,74,0.1)' }}>
            <table className="w-full text-sm">
              <thead>
                <tr style={{ backgroundColor: 'rgba(27,42,74,0.04)' }}>
                  <th className="text-left px-3 py-2 text-xs font-medium" style={{ color: 'var(--ink-muted)' }}>#</th>
                  <th className="text-left px-3 py-2 text-xs font-medium" style={{ color: 'var(--ink-muted)' }}>Fabric Code</th>
                  <th className="text-left px-3 py-2 text-xs font-medium" style={{ color: 'var(--ink-muted)' }}>For</th>
                  <th className="text-right px-3 py-2 text-xs font-medium" style={{ color: 'var(--ink-muted)' }}>Qty (m)</th>
                  <th className="text-right px-3 py-2 text-xs font-medium" style={{ color: 'var(--ink-muted)' }}>Rate</th>
                  <th className="text-right px-3 py-2 text-xs font-medium" style={{ color: 'var(--ink-muted)' }}>Amount</th>
                  {hasGst && <th className="text-right px-3 py-2 text-xs font-medium" style={{ color: 'var(--ink-muted)' }}>GST {po.gst_rate_pct}%</th>}
                  {hasGst && <th className="text-right px-3 py-2 text-xs font-medium" style={{ color: 'var(--ink-muted)' }}>Total</th>}
                  <th className="text-center px-3 py-2 text-xs font-medium" style={{ color: 'var(--ink-muted)' }}>Received</th>
                </tr>
              </thead>
              <tbody>
                {lines.map((line, i) => {
                  const isReceived = line.received_quantity != null;
                  return (
                    <tr key={line.id} className="border-t" style={{ borderColor: 'rgba(27,42,74,0.06)' }}
                      data-testid={`po-line-${line.id}`}>
                      <td className="px-3 py-2.5 text-xs" style={{ color: 'var(--ink-muted)' }}>{i + 1}</td>
                      <td className="px-3 py-2.5 font-medium" style={{ color: 'var(--navy-ink)' }}>
                        {line.fabric_code_vendor || line.fabric_code_internal}
                      </td>
                      <td className="px-3 py-2.5 text-xs" style={{ color: 'var(--navy-ink)' }}>
                        {line.client_initials}/{line.order_number}
                        <span className="text-[10px] ml-1" style={{ color: 'var(--ink-muted)' }}>({line.garment_type})</span>
                      </td>
                      <td className="px-3 py-2.5 text-right tabular-nums" style={{ color: 'var(--navy-ink)' }}>{line.quantity_metres}</td>
                      <td className="px-3 py-2.5 text-right tabular-nums" style={{ color: 'var(--ink-muted)' }}>
                        &#8377;{line.rate_per_metre?.toLocaleString('en-IN')}
                      </td>
                      <td className="px-3 py-2.5 text-right tabular-nums" style={{ color: 'var(--navy-ink)' }}>
                        &#8377;{line.amount?.toLocaleString('en-IN')}
                      </td>
                      {hasGst && (
                        <td className="px-3 py-2.5 text-right tabular-nums" style={{ color: 'var(--ink-muted)' }}>
                          &#8377;{(line.gst_amount || 0).toLocaleString('en-IN')}
                        </td>
                      )}
                      {hasGst && (
                        <td className="px-3 py-2.5 text-right font-medium tabular-nums" style={{ color: 'var(--navy-ink)' }}>
                          &#8377;{(line.total_with_gst || line.amount)?.toLocaleString('en-IN')}
                        </td>
                      )}
                      <td className="px-3 py-2.5 text-center">
                        {isReceived ? (
                          <div className="flex items-center justify-center gap-1">
                            <CheckCircle2 className="w-4 h-4" style={{ color: '#059669' }} />
                            <span className="text-xs tabular-nums" style={{ color: '#059669' }}>{line.received_quantity}m</span>
                          </div>
                        ) : (
                          <Button variant="outline" size="sm" className="h-6 px-2 text-[10px] rounded-md"
                            onClick={() => openReceiveDialog(line)}
                            data-testid={`receive-btn-${line.id}`}>
                            Receive
                          </Button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Totals */}
          <div className="flex justify-end mt-3">
            <div className="text-right space-y-1">
              {hasGst ? (
                <>
                  <div className="flex justify-between gap-8">
                    <span className="text-xs" style={{ color: 'var(--ink-muted)' }}>Subtotal ({lines.length} items, {po.total_metres}m)</span>
                    <span className="text-sm font-medium tabular-nums" style={{ color: 'var(--navy-ink)' }}>
                      &#8377;{(po.subtotal || 0).toLocaleString('en-IN')}
                    </span>
                  </div>
                  <div className="flex justify-between gap-8">
                    <span className="text-xs" style={{ color: 'var(--ink-muted)' }}>GST ({po.gst_rate_pct}%)</span>
                    <span className="text-sm font-medium tabular-nums" style={{ color: 'var(--ink-muted)' }}>
                      &#8377;{(po.gst_amount || 0).toLocaleString('en-IN')}
                    </span>
                  </div>
                  <div className="border-t pt-1" style={{ borderColor: 'rgba(27,42,74,0.1)' }}>
                    <div className="flex justify-between gap-8">
                      <span className="text-xs font-semibold" style={{ color: 'var(--brand-navy)' }}>Grand Total</span>
                      <span className="text-xl font-bold tabular-nums" style={{ color: 'var(--brand-navy)' }}>
                        &#8377;{po.total_amount?.toLocaleString('en-IN')}
                      </span>
                    </div>
                  </div>
                </>
              ) : (
                <>
                  <p className="text-xs" style={{ color: 'var(--ink-muted)' }}>Total ({lines.length} items, {po.total_metres}m)</p>
                  <p className="text-xl font-bold tabular-nums" style={{ color: 'var(--brand-navy)' }}>
                    &#8377;{po.total_amount?.toLocaleString('en-IN')}
                  </p>
                </>
              )}
            </div>
          </div>

          {po.notes && (
            <>
              <Separator className="my-4" />
              <p className="text-xs" style={{ color: 'var(--ink-muted)' }}>
                <strong>Notes:</strong> {po.notes}
              </p>
            </>
          )}
        </CardContent>
      </Card>

      {/* Receive + Inline QC Dialog */}
      <Dialog open={!!receiveDialog} onOpenChange={(open) => { if (!open) { setReceiveDialog(null); setQcStep(false); } }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle style={{ color: 'var(--brand-navy)' }}>
              {qcStep ? 'Fabric Receipt QC' : 'Receive Fabric'}
            </DialogTitle>
          </DialogHeader>
          {receiveDialog && !qcStep && (
            <div className="space-y-4">
              <div className="p-3 rounded-lg" style={{ backgroundColor: 'rgba(27,42,74,0.03)' }}>
                <p className="text-sm font-medium" style={{ color: 'var(--navy-ink)' }}>
                  {receiveDialog.fabric_code_vendor || receiveDialog.fabric_code_internal}
                </p>
                <p className="text-xs" style={{ color: 'var(--ink-muted)' }}>
                  Ordered: {receiveDialog.quantity_metres}m
                </p>
              </div>
              <div>
                <Label className="text-xs font-medium" style={{ color: 'var(--ink-muted)' }}>Received Quantity (m)</Label>
                <Input type="number" step="0.1" value={recvQty} onChange={(e) => setRecvQty(e.target.value)}
                  className="h-10 mt-1 rounded-lg" autoFocus data-testid="receive-qty-input" />
              </div>
              <div>
                <Label className="text-xs font-medium" style={{ color: 'var(--ink-muted)' }}>Received Date</Label>
                <Input type="date" value={recvDate} onChange={(e) => setRecvDate(e.target.value)}
                  className="h-10 mt-1 rounded-lg" data-testid="receive-date-input" />
              </div>
            </div>
          )}
          {receiveDialog && qcStep && (
            <div className="space-y-3">
              <div className="p-2.5 rounded-lg" style={{ backgroundColor: 'rgba(27,42,74,0.03)' }}>
                <p className="text-xs" style={{ color: 'var(--ink-muted)' }}>
                  {receiveDialog.fabric_code_vendor || receiveDialog.fabric_code_internal} — {recvQty}m received on {recvDate}
                </p>
              </div>
              <div className="space-y-2">
                {FABRIC_QC_CHECKS.map(check => {
                  const val = qcChecks[check.key] || 'pass';
                  const isFail = val === 'fail';
                  return (
                    <div key={check.key} className="flex items-center justify-between py-1.5 px-2 rounded-lg border"
                      style={{ borderColor: isFail ? 'rgba(220,38,38,0.2)' : 'rgba(27,42,74,0.08)', backgroundColor: isFail ? 'rgba(220,38,38,0.03)' : 'transparent' }}
                      data-testid={`qc-check-${check.key}`}>
                      <span className="text-xs" style={{ color: 'var(--navy-ink)' }}>{check.check_name}</span>
                      <div className="flex gap-1">
                        <button
                          className={`px-2 py-0.5 rounded text-[10px] font-medium transition-colors ${val === 'pass' ? 'bg-green-100 text-green-700' : 'bg-gray-50 text-gray-400 hover:bg-green-50'}`}
                          onClick={() => setQcChecks(prev => ({ ...prev, [check.key]: 'pass' }))}
                          data-testid={`qc-pass-${check.key}`}>
                          Pass
                        </button>
                        <button
                          className={`px-2 py-0.5 rounded text-[10px] font-medium transition-colors ${val === 'fail' ? 'bg-red-100 text-red-700' : 'bg-gray-50 text-gray-400 hover:bg-red-50'}`}
                          onClick={() => setQcChecks(prev => ({ ...prev, [check.key]: 'fail' }))}
                          data-testid={`qc-fail-${check.key}`}>
                          Fail
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
              {Object.values(qcChecks).some(v => v === 'fail') && (
                <div className="p-2.5 rounded-lg flex items-start gap-2" style={{ backgroundColor: 'rgba(220,38,38,0.04)', border: '1px solid rgba(220,38,38,0.15)' }}>
                  <AlertTriangle className="w-4 h-4 text-red-500 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-xs font-medium text-red-700">QC issues detected</p>
                    <p className="text-[10px] text-red-600 mt-0.5">Receipt will be recorded but flagged for review on the order.</p>
                  </div>
                </div>
              )}
              <div>
                <Label className="text-xs font-medium" style={{ color: 'var(--ink-muted)' }}>QC Notes (optional)</Label>
                <Textarea value={qcNotes} onChange={e => setQcNotes(e.target.value)} className="mt-1 text-xs h-16"
                  placeholder="Any observations..." data-testid="qc-notes-input" />
              </div>
            </div>
          )}
          <DialogFooter>
            {qcStep && (
              <Button variant="outline" onClick={() => setQcStep(false)} className="mr-auto">Back</Button>
            )}
            <Button variant="outline" onClick={() => { setReceiveDialog(null); setQcStep(false); }}>Cancel</Button>
            {!qcStep ? (
              <Button onClick={handleProceedToQC} disabled={!recvQty}
                className="gap-1 rounded-lg" style={{ backgroundColor: 'var(--brand-navy)' }}
                data-testid="proceed-to-qc">
                Next: QC Check
              </Button>
            ) : (
              <Button onClick={handleReceive} disabled={receiving}
                className="gap-1 rounded-lg" style={{ backgroundColor: Object.values(qcChecks).some(v => v === 'fail') ? '#DC2626' : 'var(--brand-navy)' }}
                data-testid="confirm-receive">
                {Object.values(qcChecks).some(v => v === 'fail') ? (
                  <><XCircle className="w-3.5 h-3.5" /> {receiving ? 'Saving...' : 'Receive with Issues'}</>
                ) : (
                  <><CheckCircle2 className="w-3.5 h-3.5" /> {receiving ? 'Saving...' : 'Confirm Receipt & QC'}</>
                )}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Print styles for PO
const printStyles = `
  @media print {
    body * { visibility: hidden; }
    [data-testid="po-document"], [data-testid="po-document"] * { visibility: visible; }
    [data-testid="po-document"] { position: absolute; left: 0; top: 0; width: 100%; }
    .no-print, [data-testid="back-to-pos"], button { display: none !important; }
    [data-testid="received-badge"], [data-testid="receive-btn"] { display: none !important; }
    @page { margin: 1cm; size: A4; }
  }
`;

// Inject print styles
if (typeof document !== 'undefined') {
  const styleEl = document.createElement('style');
  styleEl.textContent = printStyles;
  document.head.appendChild(styleEl);
}
